import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PreconsumerScrapMaterial,
  PreconsumerScrapMaterialCreatePayload,
  PreconsumerScrapMaterialUpdatePayload,
  PreconsumerScrapMaterialWithEmissionReport
} from '@/api/PreconsumerScrapMaterialModel'
import { apiClient } from '@/shared/api/apiClient'
import { EMISSION_REPORTS_QUERY_KEY } from '@/hooks/api/emissionReports'

const queryKey = 'preconsumerScrapMaterials'
const queryKeyPreconsumerScrapMaterialsBalance = 'preconsumerScrapMaterialsBalance'

export const useCreatePreconsumerScrapMaterial = (installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation<PreconsumerScrapMaterial, Error, PreconsumerScrapMaterialCreatePayload>({
    mutationFn: async (payload: PreconsumerScrapMaterialCreatePayload) => {
      const response = await apiClient.post<PreconsumerScrapMaterial>(
        `/v1/installations/${installationId}/preconsumer-scrap-materials`,
        payload
      )
      return response.data
    },
    onSuccess: (_data, { emissionReportId }) => {
      if (emissionReportId) {
        queryClient.refetchQueries({
          queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId]
        })
      }

      queryClient.refetchQueries({
        queryKey: [queryKeyPreconsumerScrapMaterialsBalance, installationId]
      })

      queryClient.refetchQueries({
        queryKey: [queryKey, installationId]
      })
    }
  })
}

export const useListScrapMaterials = (installationId: string) => {
  return useQuery({
    queryKey: [queryKey, installationId],
    queryFn: async () => {
      const response = await apiClient.get<PreconsumerScrapMaterialWithEmissionReport[]>(
        `/v1/installations/${installationId}/preconsumer-scrap-materials`
      )

      return response.data
    }
  })
}

export const useDeletePreconsumerScrapMaterial = (emissionReportId?: string) => {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { scrapMaterialId: string }>({
    mutationFn: async ({ scrapMaterialId }) => {
      await apiClient.delete(`/v1/preconsumer-scrap-materials/${scrapMaterialId}`)
    },
    onSuccess: () => {
      if (emissionReportId) {
        queryClient.refetchQueries({
          queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId]
        })

        return
      }

      queryClient.refetchQueries({
        queryKey: [queryKey]
      })
      queryClient.refetchQueries({
        queryKey: [queryKeyPreconsumerScrapMaterialsBalance]
      })
    }
  })
}

export const useUpdatePreconsumerScrapMaterial = (emissionReportId?: string) => {
  const queryClient = useQueryClient()
  return useMutation<
    PreconsumerScrapMaterial,
    Error,
    { scrapMaterialId: string; payload: PreconsumerScrapMaterialUpdatePayload }
  >({
    mutationFn: async ({ scrapMaterialId, payload }) => {
      const response = await apiClient.put<PreconsumerScrapMaterial>(
        `/v1/preconsumer-scrap-materials/${scrapMaterialId}`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      if (emissionReportId) {
        queryClient.refetchQueries({
          queryKey: [EMISSION_REPORTS_QUERY_KEY, emissionReportId]
        })

        return
      }

      queryClient.refetchQueries({
        queryKey: [queryKey]
      })
      queryClient.refetchQueries({
        queryKey: [queryKeyPreconsumerScrapMaterialsBalance]
      })
    }
  })
}

export const useGetScrapTypes = (installationId: string) => {
  return useQuery({
    queryKey: ['scrapTypes', installationId],
    queryFn: async () => {
      const response = await apiClient.get<string[]>(
        `/v1/installations/${installationId}/preconsumer-scrap-materials/scrap-types`
      )
      return response.data
    }
  })
}
